import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import useCounterAnimation from "../hooks/useCounterAnimation";
import useScrollAnimation from "../hooks/useScrollAnimation";

const HeroSection = () => {
  const [pageviewsCount, pageviewsRef] = useCounterAnimation("990M+", 2500, 500);
  const [videoViewsCount, videoViewsRef] = useCounterAnimation("20M+", 2000, 600);
  const [isVisible, sectionRef] = useScrollAnimation(0.2);
  const [showLabels, setShowLabels] = useState(false);

  // Show labels after the longest counter animation finishes
  useEffect(() => {
    if (isVisible) {
      setShowLabels(false); // Ensure labels start hidden
      const timer = setTimeout(() => {
        setShowLabels(true);
      }, 3100); // Wait for longest animation (2500ms + 600ms delay)

      return () => clearTimeout(timer);
    } else {
      setShowLabels(false); // Reset when not visible
    }
  }, [isVisible]);

  return (
    <section 
      ref={sectionRef}
      className={`relative min-h-screen flex items-center justify-center overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.7)), url("https://customer-assets.emergentagent.com/job_xd-project-landing/artifacts/8mdizrzf_Stage_Background.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'scroll'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-black text-white leading-none tracking-tight">
            WE KNOW
            <span className="block text-yellow-500">MINING</span>
          </h1>
        </div>
        
        <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
          Kitco is the Leader in Mining Coverage
        </p>

        {/* Stats Bar */}
        <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-16 mt-12">
          <div className="text-center group relative">
            <div className="text-3xl md:text-4xl font-bold text-yellow-500 group-hover:scale-110 transition-transform duration-300 min-h-[3rem] flex items-center justify-center w-32 mx-auto">
              <span ref={pageviewsRef}>{pageviewsCount}</span>
            </div>
            <div className={`text-white text-sm md:text-base mt-2 h-6 flex items-center justify-center transition-opacity duration-500 ${
              showLabels ? 'opacity-100' : 'opacity-0'
            }`}>
              Page Views Per Year
            </div>
            {/* Hover Source */}
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-60 transition-opacity duration-300 pointer-events-none">
              <p className="text-gray-400 text-xs whitespace-nowrap">
                Google Analytics - Last 12 months Sept. 2025
              </p>
            </div>
          </div>
          
          <div className="hidden md:block w-px h-12 bg-yellow-500/30 animate-pulse"></div>
          
          <div className="text-center group relative">
            <div className="text-3xl md:text-4xl font-bold text-yellow-500 group-hover:scale-110 transition-transform duration-300 min-h-[3rem] flex items-center justify-center w-32 mx-auto">
              <span ref={videoViewsRef}>{videoViewsCount}</span>
            </div>
            <div className={`text-white text-sm md:text-base mt-2 h-6 flex items-center justify-center transition-opacity duration-500 ${
              showLabels ? 'opacity-100' : 'opacity-0'
            }`}>
              Video Views Per Year
            </div>
            {/* Hover Source */}
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-60 transition-opacity duration-300 pointer-events-none">
              <p className="text-gray-400 text-xs whitespace-nowrap">
                YouTube - Last 12 months Sept. 2025
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <Button 
            size="lg" 
            className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold px-8 py-3 text-lg"
            onClick={() => {
              document.getElementById('contact')?.scrollIntoView({ 
                behavior: 'smooth' 
              });
            }}
          >
            Learn More About Our Reach
          </Button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;